import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../../api/client';
import type { ProjectTargetUniverseData, TargetNode, UniverseNode } from '../../types/targetUniverse';
import TargetUniverse3D from './compound-tabs/target-universe/TargetUniverse3D';
import TargetUniverse2D from './compound-tabs/target-universe/TargetUniverse2D';
import SelectivityRadar from './compound-tabs/target-universe/SelectivityRadar';
import SeriesHeatmap from './compound-tabs/target-universe/SeriesHeatmap';

type SubView = 'scatter' | 'globe' | 'radar' | 'heatmap';

const TOP_N_OPTIONS = [10, 20, 50] as const;

interface ProjectTargetUniverseSectionProps {
  projectId: number;
}

const SUB_TABS: { id: SubView; label: string; icon: string; tooltip: string }[] = [
  { id: 'scatter', label: 'How Selective?', icon: '\u2299', tooltip: 'Compound selectivity across target panel' },
  { id: 'globe', label: 'Biological Network', icon: '\u25C9', tooltip: 'Target relationships in pathway context' },
  { id: 'radar', label: 'Safety Radar', icon: '\u25C7', tooltip: 'Off-target safety profile at a glance' },
  { id: 'heatmap', label: 'SAR Map', icon: '\u25A6', tooltip: 'Structure-activity across all targets' },
];

const ProjectTargetUniverseSection: React.FC<ProjectTargetUniverseSectionProps> = ({ projectId }) => {
  const [data, setData] = useState<ProjectTargetUniverseData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subView, setSubView] = useState<SubView>('globe');
  const [fetched, setFetched] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [topN, setTopN] = useState(20);
  const [sourceFilter, setSourceFilter] = useState<Set<string>>(new Set(['genetic', 'phenotypic']));

  const toggleSource = (source: string) => {
    setSourceFilter(prev => {
      const next = new Set(prev);
      if (next.has(source)) {
        if (next.size > 1) next.delete(source);
      } else {
        next.add(source);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!projectId || fetched) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`/v2/projects/${projectId}/target-universe`);
        setData(res.data);
        setFetched(true);
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Failed to load target universe');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, fetched]);

  // Filter targets by source type: binding assays (IC50/Ki/Kd) = genetic, functional (EC50) = phenotypic
  const filteredBySource = useMemo(() => {
    if (!data) return [];
    if (sourceFilter.size === 2) return data.targets;
    const wantGenetic = sourceFilter.has('genetic');
    return data.targets.filter(t => {
      const isGenetic = t.activity_type !== 'EC50';
      return wantGenetic ? isGenetic : !isGenetic;
    });
  }, [data, sourceFilter]);

  // Map ProjectTargetNode → TargetNode for sub-view compatibility
  const mappedTargets: TargetNode[] = useMemo(() => {
    return filteredBySource.map(t => ({
      target_name: t.target_name,
      chembl_id: t.chembl_id,
      uniprot_id: t.uniprot_id,
      activity_type: t.activity_type,
      activity_value: t.best_activity_value,
      activity_units: t.activity_units,
      pIC50: t.best_pIC50,
      source: t.source,
      pathways: t.pathways,
      is_primary: t.is_primary,
      safety_flag: t.safety_flag,
      compound_count: t.compound_count,
    }));
  }, [filteredBySource]);

  // Build UniverseNode[] from mapped targets
  const allNodes: UniverseNode[] = useMemo(() => {
    return mappedTargets.map(t => ({
      id: t.chembl_id || t.target_name,
      name: t.target_name,
      fullName: t.target_name,
      uniprot: t.uniprot_id,
      isProjectTarget: t.is_primary,
      pIC50: t.pIC50 || 5,
      activityNM: t.activity_value || Math.pow(10, 9 - (t.pIC50 || 5)),
      known: true,
      pathways: t.pathways || [],
      safetyFlag: t.safety_flag,
      x: 0, y: 0, z: 0,
    }));
  }, [mappedTargets]);

  const projectPIC50 = useMemo(() =>
    allNodes.find(n => n.isProjectTarget)?.pIC50 || 7,
    [allNodes],
  );

  // Filter to top N
  const filteredNodes = useMemo(() => {
    const projectNode = allNodes.find(n => n.isProjectTarget);
    const others = [...allNodes]
      .filter(n => !n.isProjectTarget)
      .sort((a, b) => b.pIC50 - a.pIC50)
      .slice(0, topN);
    return projectNode ? [projectNode, ...others] : others;
  }, [allNodes, topN]);

  // Filtered mapped targets for radar/heatmap — respects topN
  const filteredMappedTargets: TargetNode[] = useMemo(() => {
    const filteredIds = new Set(filteredNodes.map(n => n.id));
    return mappedTargets.filter(t => filteredIds.has(t.chembl_id || t.target_name));
  }, [filteredNodes, mappedTargets]);

  // Filtered source data for the table — respects topN
  const filteredTableTargets = useMemo(() => {
    const filteredIds = new Set(filteredNodes.map(n => n.id));
    return filteredBySource.filter(t => filteredIds.has(t.chembl_id || t.target_name));
  }, [filteredNodes, filteredBySource]);

  const showTopN = true; // Show Top N filter for all views

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Building target universe across project...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6 text-center">
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--hover-bg)' }}>
          <svg className="w-6 h-6" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
            <circle cx="12" cy="12" r="4" strokeWidth={1.5} />
            <line x1="12" y1="2" x2="12" y2="8" strokeWidth={1.5} />
            <line x1="12" y1="16" x2="12" y2="22" strokeWidth={1.5} />
          </svg>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Biological network visualization unavailable</p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Target data may still appear in the table below</p>
      </div>
    );
  }

  if (!data || data.targets.length === 0) {
    return (
      <div className="py-6 text-center">
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          No ChEMBL target data found across project compounds.
        </p>
      </div>
    );
  }

  const risk = data.risk_summary;

  return (
    <div className="space-y-4">
      {/* Risk Summary Banner */}
      {risk?.primary_target && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Project Risk Summary</span>
            <span className="ml-auto text-xs text-gray-400">
              {data.compound_count} compound{data.compound_count !== 1 ? 's' : ''} analysed
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-gray-400 text-xs">Primary target</span>
              <p className="font-medium text-cyan-500">
                {risk.primary_target}
                {risk.primary_pIC50 != null && <span className="font-mono ml-1 text-xs">pIC50 {risk.primary_pIC50}</span>}
              </p>
            </div>
            {risk.worst_offtarget && (
              <div>
                <span className="text-gray-400 text-xs">Worst off-target</span>
                <p className="font-medium text-amber-500">
                  {risk.worst_offtarget}
                  {risk.worst_offtarget_pIC50 != null && <span className="font-mono ml-1 text-xs">pIC50 {risk.worst_offtarget_pIC50}</span>}
                </p>
              </div>
            )}
            {risk.selectivity_window != null && (
              <div>
                <span className="text-gray-400 text-xs">Selectivity window</span>
                <p className={`font-mono font-medium ${
                  risk.selectivity_window >= 2 ? 'text-emerald-500' :
                  risk.selectivity_window >= 1 ? 'text-amber-500' : 'text-red-500'
                }`}>
                  {risk.selectivity_window > 0 ? '+' : ''}{risk.selectivity_window} log
                </p>
              </div>
            )}
            {risk.safety_flags.length > 0 && (
              <div>
                <span className="text-gray-400 text-xs">Safety flags</span>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {risk.safety_flags.map((f, i) => (
                    <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sub-view tab bar with Top N */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSubView(tab.id)}
            title={tab.tooltip}
            className="px-3 py-2 text-sm whitespace-nowrap transition-colors"
            style={{
              color: subView === tab.id ? '#0D9488' : undefined,
              borderBottom: subView === tab.id ? '2px solid #0D9488' : '2px solid transparent',
              fontWeight: subView === tab.id ? 600 : 400,
            }}
          >
            <span className="mr-1">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1.5 pr-2">
          <button onClick={() => toggleSource('genetic')}
            className="px-2.5 py-0.5 text-xs rounded-full transition-colors"
            style={{
              background: sourceFilter.has('genetic') ? 'rgba(13,148,136,0.15)' : 'transparent',
              color: sourceFilter.has('genetic') ? '#0D9488' : undefined,
              fontWeight: sourceFilter.has('genetic') ? 600 : 400,
              border: sourceFilter.has('genetic') ? '1px solid rgba(13,148,136,0.3)' : '1px solid transparent',
            }}>
            Genetic
          </button>
          <button onClick={() => toggleSource('phenotypic')}
            className="px-2.5 py-0.5 text-xs rounded-full transition-colors"
            style={{
              background: sourceFilter.has('phenotypic') ? 'rgba(13,148,136,0.15)' : 'transparent',
              color: sourceFilter.has('phenotypic') ? '#0D9488' : undefined,
              fontWeight: sourceFilter.has('phenotypic') ? 600 : 400,
              border: sourceFilter.has('phenotypic') ? '1px solid rgba(13,148,136,0.3)' : '1px solid transparent',
            }}>
            Phenotypic
          </button>
          <span className="text-gray-300 dark:text-gray-600 mx-0.5">|</span>
          {showTopN ? (
            <>
              <span className="text-xs text-gray-400">{filteredNodes.length} of {allNodes.length}</span>
              {TOP_N_OPTIONS.map(n => (
                <button key={n} onClick={() => setTopN(n)}
                  className="px-2 py-0.5 text-xs rounded"
                  style={{
                    background: topN === n ? 'rgba(13,148,136,0.15)' : 'transparent',
                    color: topN === n ? '#0D9488' : undefined,
                    fontWeight: topN === n ? 600 : 400,
                  }}>
                  Top {n}
                </button>
              ))}
            </>
          ) : (
            <span className="text-xs text-gray-400">
              {filteredBySource.length} target{filteredBySource.length !== 1 ? 's' : ''} across {data.compound_count} compounds
            </span>
          )}
        </div>
      </div>

      {/* Sub-view content — capped at 60vh so target table is always visible */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4" style={{ maxHeight: '60vh', overflow: 'auto' }}>
        {subView === 'globe' && (
          <TargetUniverse3D
            nodes={filteredNodes}
            selectedTarget={selectedTarget}
            onSelectTarget={setSelectedTarget}
            projectPIC50={projectPIC50}
          />
        )}
        {subView === 'scatter' && (
          <TargetUniverse2D
            nodes={filteredNodes}
            selectedTarget={selectedTarget}
            onSelectTarget={setSelectedTarget}
            projectPIC50={projectPIC50}
          />
        )}
        {subView === 'radar' && (
          <SelectivityRadar targets={filteredMappedTargets} />
        )}
        {subView === 'heatmap' && (
          <SeriesHeatmap
            seriesData={data.series_data}
            currentMoleculeId={-1}
            targets={filteredMappedTargets}
          />
        )}
      </div>

      {/* Target list with compound counts — scrollable with sticky header */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
              <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Target</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">UniProt</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-300"># Compounds</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Best pIC50</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Safety</th>
            </tr>
          </thead>
          <tbody>
            {filteredTableTargets.map((t, i) => (
              <tr key={i} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-medium text-gray-800 dark:text-gray-200">{t.target_name}</span>
                  {t.chembl_id && (
                    <a href={`https://www.ebi.ac.uk/chembl/target_report_card/${t.chembl_id}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-xs text-gray-400 hover:text-blue-500 hover:underline">
                      {t.chembl_id}
                    </a>
                  )}
                </td>
                <td className="px-4 py-3">
                  {t.uniprot_id ? (
                    <a href={`https://www.uniprot.org/uniprot/${t.uniprot_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 hover:underline">
                      {t.uniprot_id}
                    </a>
                  ) : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-semibold">
                    {t.compound_count}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-gray-700 dark:text-gray-300">
                  {t.best_pIC50 != null ? t.best_pIC50.toFixed(1) : '-'}
                </td>
                <td className="px-4 py-3">
                  {t.safety_flag ? (
                    <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs font-medium">
                      {t.safety_flag}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectTargetUniverseSection;
